import { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { Waiting } from "./Shell";
import { SectionHead } from "./SectionHead";
import { dateTime } from "../lib/ct";

// The Gatekeeper — people & their verification, sharing and conflicts. It SCORES and EXPLAINS but
// admits nobody by itself: owner sign-up is a recorded fact (a seat, never a claim on a building);
// broker/lender/refer/conflict form a view and a named human decides; it refers, never revokes.
export default function TheGatekeeper() {
  const { authedFetch } = useAuth();
  const { waiting, loaded } = useContext(Waiting);
  const onYou = waiting["gatekeeper"] || 0;
  const [tab, setTab] = useState("conflicts");
  const [v, setV] = useState(null);
  const [failures, setFailures] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    let alive = true;
    authedFetch("/gatekeeper/queue").then((d) => alive && setV(d)).catch((e) => alive && setErr(String(e)));
    authedFetch("/gatekeeper/sign-in-failures").then((d) => alive && setFailures(d)).catch(() => {});
    return () => { alive = false; };
  }, [authedFetch]);
  useEffect(load, [load]);

  if (err) return <div className="gk-err" data-testid="gatekeeper-error">{err}</div>;
  if (!v) return <div className="loading" data-testid="gatekeeper-loading">Loading…</div>;

  // F128 (founder ruling 30 Sep, built 11 Oct): the VERIFICATION tab is gone, not empty.
  // `verification_case` has no writer in R1 — owner sign-up is a recorded fact, never an admission
  // decision — so a panel for it could only ever be an honest-looking zero about a thing that cannot
  // exist yet. A disputed claim now reads `building_claim.state == "conflict"`, server-side.
  const counts = {
    sharing: v.sharing.scan.opened_from_real_data.length,
    conflicts: v.conflicts.length,
    b9: v.b9.buildings_added_rate_flags.length + v.b9.name_vs_deed_soft_flags.length,
    signins: failures ? failures.counts.last_24h : 0,
  };
  const waitingOnYou = counts.sharing + counts.conflicts;

  return (
    <div data-testid="the-gatekeeper">
      <SectionHead eyebrow="THE GATEKEEPER · PEOPLE AND ACTIVITY" testid="gk"
        headline={!loaded
          ? `${waitingOnYou} in the queue.`
          : onYou > 0
            ? `${onYou} ${onYou === 1 ? "case is" : "cases are"} waiting on your decision.`
            : "Nobody is waiting on a decision."}
        lede={v.fixtures_included && waitingOnYou > 0
          ? `${waitingOnYou} labelled ${waitingOnYou === 1 ? "fixture carries" : "fixtures carry"} this surface in dev — never a request.`
          : null}>
        <p className="gk-constraint" data-testid="gatekeeper-constraint">{v.constraint}</p>
        {v.fixtures_included && (
          <div className="gk-fixture-banner" data-testid="gatekeeper-fixture-banner">
            Showing labelled fixtures — dev review only, never promotable, production never seeded.
          </div>
        )}
      </SectionHead>

      <div role="tablist" className="tabs">
        {[["sharing", "Sharing"], ["conflicts", "Conflicts"], ["b9", "Open-door signals"], ["signins", "Failed sign-ins"]].map(([id, label]) => (
          <button key={id} role="tab" data-testid={`gk-tab-${id}`} aria-selected={tab === id}
                  className="tab" onClick={() => setTab(id)}>
            {label}<span className="tab-count">{counts[id]}</span>
          </button>
        ))}
      </div>

      {tab === "sharing" && <Sharing sharing={v.sharing} />}
      {tab === "conflicts" && <Conflicts conflicts={v.conflicts} />}
      {tab === "b9" && <B9 b9={v.b9} />}
      {tab === "signins" && <SignInFailures data={failures} />}
    </div>
  );
}

// F128: `Verification` and `CaseCard` are REMOVED with the tab they served — a panel for a
// collection with no writer is not left behind as dead code either.
function Sharing({ sharing }) {
  return (
    <div className="gk-panel" data-testid="gatekeeper-sharing">
      <p className="gk-note">{sharing.scan.note}</p>
      <table className="gk-table" data-testid="gatekeeper-sharing-table">
        <thead><tr><th>account</th><th>principal</th><th>devices</th><th>sessions</th><th>signals</th><th></th></tr></thead>
        <tbody>
          {sharing.scan.scanned.map((r) => (
            <tr key={r.account_id} data-testid={`gk-scan-${r.account_id}`}>
              <td className="mono">{r.account_id.slice(0, 12)}</td>
              <td>{r.principal}{r.staff_dogfood && <span className="gk-tag">staff · dogfood</span>}</td>
              <td>{r.distinct_devices}</td>
              <td>{r.sessions}</td>
              <td>{r.signals.join(", ") || "—"}</td>
              <td>{r.staff_dogfood ? <span className="gk-muted">never member evidence</span> : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="gk-note">
        Opened member cases: {sharing.scan.opened_from_real_data.length === 0
          ? "none — a case opens only for a non-staff account with ≥2 independent signals."
          : sharing.scan.opened_from_real_data.map((o) => o.account_id).join(", ")}
      </p>
    </div>
  );
}

function Conflicts({ conflicts }) {
  return (
    <div className="gk-panel" data-testid="gatekeeper-conflicts">
      {conflicts.length === 0
        ? <div className="empty-state"><p>No open conflicts.</p></div>
        : conflicts.map((c) => (
            <div key={c.id} className="held-card" data-testid={`gk-conflict-${c.id}`}>
              <div className="held-head">
                <span className="held-kind">competing claim</span>
                <span className="held-field">parcel <strong>{c.parcel_id}</strong></span>
                {c.both_suppressed_while_open && <span className="status-pill sp-input_needed">both suppressed while open</span>}
              </div>
              <ul className="gk-checks">
                {/* A real building_claim conflict names its claimants and carries NO adjudication
                    clock; a row with neither must render, not throw (F128 crash, 11 Oct). */}
                {(c.claimants || []).map((cl) => (
                  <li key={cl.party_identity_id} className="gk-check gk-warn">
                    <span className="gk-check-label">{cl.label}</span>
                    <span className="gk-check-status mono">{cl.party_identity_id}</span>
                  </li>
                ))}
              </ul>
              <p className="gk-note">{c.deadline ? `Deadline ${c.deadline} · ` : ""}a named human
                adjudicates; a false claim is recorded against the identity. The Gatekeeper does not
                decide this by itself.</p>
            </div>
          ))}
    </div>
  );
}

function SignInFailures({ data }) {
  if (!data) return <div className="loading" data-testid="gk-signins-loading">Loading…</div>;
  const { rows, counts, by_cause_7d: byCause, note, limit } = data;
  const when = (iso) => dateTime(iso);   // F134: Central, and the zone is named
  return (
    <div className="gk-panel" data-testid="gatekeeper-signins">
      <p className="gk-note" data-testid="gk-signins-note">{note}</p>
      {!data.recording_enabled && (
        <div className="gk-fixture-banner" data-testid="gk-signins-off">
          Recording is off until the sign-in audit key is set in this environment. Sign-in is
          unaffected; nothing is being written, and no unkeyed digest is ever stored.
        </div>
      )}
      <div className="gk-signin-counts" data-testid="gk-signins-counts">
        <span><strong>{counts.last_24h}</strong> in the last 24 hours</span>
        <span><strong>{counts.last_7d}</strong> in the last 7 days</span>
        <span><strong>{counts.total}</strong> since the log began</span>
      </div>
      <ul className="gk-signin-causes" data-testid="gk-signins-by-cause">
        {Object.entries(byCause).map(([cause, n]) => (
          <li key={cause} data-testid={`gk-signins-cause-${cause}`}>
            <span className="mono">{cause}</span><span className="tab-count">{n}</span>
          </li>
        ))}
      </ul>
      {rows.length === 0
        ? <div className="empty-state"><p data-testid="gk-signins-empty">No refused sign-in has been recorded.</p></div>
        : (
          <table className="gk-table" data-testid="gk-signins-table">
            <thead><tr><th>when</th><th>cause</th><th>who they signed in as</th><th>email (hashed)</th><th>IP</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} data-testid={`gk-signin-row-${i}`}>
                  <td>{when(r.at)}</td>
                  <td>{r.cause_label}</td>
                  <td>{r.principal}</td>
                  <td className="mono" title={r.email_hash}>{(r.email_hash || "").slice(0, 16)}</td>
                  <td className="mono">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      {rows.length >= limit && (
        <p className="gk-muted" data-testid="gk-signins-truncated">
          Showing the newest {limit}. Every row is kept — nothing here is ever deleted.
        </p>
      )}
    </div>
  );
}

function B9({ b9 }) {
  const Row = ({ title, items, render }) => (
    <div className="gk-b9-block">
      <h3>{title}<span className="tab-count">{items.length}</span></h3>
      {items.length === 0 ? <p className="gk-muted">none</p> : <ul className="gk-b9-list">{items.map(render)}</ul>}
    </div>
  );
  return (
    <div className="gk-panel" data-testid="gatekeeper-b9">
      <p className="gk-note">The open front door, instrumented — recorded facts and soft flags, never
        a block in R1. Owner sign-up is here because it is recorded, not assessed.</p>
      <Row title="Owner sign-ups (recorded facts)" items={b9.owner_signups_recorded}
           render={(o, i) => <li key={i}><strong>{o.name}</strong> · mobile {o.mobile} · {o.note}</li>} />
      <Row title="Buildings-added-rate flags" items={b9.buildings_added_rate_flags}
           render={(o, i) => <li key={i} className="gk-warn-text">{o.account_id.slice(0, 12)} · {o.added_24h} in 24h · {o.note}</li>} />
      <Row title="Name-vs-deed soft flags" items={b9.name_vs_deed_soft_flags}
           render={(o, i) => <li key={i} className="gk-warn-text">{o.account_id.slice(0, 12)} · parcel {o.parcel_id} · {o.note}</li>} />
    </div>
  );
}
